import { getSupportToken, SUPPORT_HEADER } from "../../features/workspace/support-session";
import type { CsvFile, Locale } from "../types";
import { ApiError, errorMeta } from "../errors";

const GET_RETRY_DELAY_MS = 300;

/** FB2-01: how long a shell/global read stays fresh before it is fetched again. */
export const SHELL_GET_CACHE_TTL_MS = 15_000;
/**
 * FB2-01: shell/global read endpoints that every page re-asks for (header chips, sidebar, banners).
 * Only these are cached (matched by path suffix, query ignored); every other GET is only
 * de-duplicated while in flight.
 */
const SHELL_GET_SUFFIX =
  /\/(?:auth\/me|ui-prefs|entitlements|billing\/wallet|billing\/usage|onboarding\/(?:checklist|sample-data|upsell-prompt)|catalogs\/availability|notifications\/unread-count|automation\/pause|legal\/pending|channels)$/;
const WORKSPACE_IN_PATH = /^\/v1\/workspaces\/([^/?]+)/;

function isShellCacheable(path: string): boolean {
  const bare = path.split("?")[0] ?? path;
  return (
    bare === "/v1/workspaces" ||
    /^\/v1\/workspaces\/[^/]+$/.test(bare) ||
    SHELL_GET_SUFFIX.test(bare)
  );
}
const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export interface HttpApiClientOptions {
  /** API origin; empty = same origin (Vite proxies `/v1` in dev, the ingress in prod). */
  readonly baseUrl?: string;
  /** Current UI locale, sent as `Accept-Language` so API errors come back localized. */
  readonly locale: () => Locale;
  readonly fetch?: typeof fetch;
  /** FB3-47: called for every failed write (non-GET) so no failure is ever silent. */
  readonly onMutationError?: (error: ApiError, request: { method: string; path: string }) => void;
  /**
   * Q4-XCT-02: called for the first 401 from any API call outside `/v1/auth/*` (an expired
   * session). Later 401s are ignored until the client is recreated, so it fires once.
   */
  readonly onUnauthorized?: (error: ApiError, request: { method: string; path: string }) => void;
}

interface ErrorBody {
  error?: { code?: unknown; message?: unknown; details?: unknown };
}

/**
 * Request pipeline of the `fetch`-based client. Authentication is the HttpOnly `wato_dash_sid` cookie
 * (`credentials: "include"`); the dashboard never stores tokens. The browser adds `Origin`
 * to state-changing requests, which the API checks (CSRF defence in depth).
 */
export function createTransport(options: HttpApiClientOptions): HttpTransport {
  const base = (options.baseUrl ?? "").replace(/\/+$/, "");
  const doFetch = options.fetch ?? ((input, init) => globalThis.fetch(input, init));

  let unauthorizedNotified = false;
  function noteUnauthorized(error: ApiError, method: string, path: string): void {
    if (error.status !== 401 || path.startsWith("/v1/auth") || unauthorizedNotified) return;
    unauthorizedNotified = true;
    options.onUnauthorized?.(error, { method, path });
  }

  async function performRequest<T>(
    method: string,
    path: string,
    body?: unknown,
    raw?: { body: Blob; contentType: string },
    extraHeaders?: Record<string, string>,
  ): Promise<T> {
    const headers: Record<string, string> = {
      accept: "application/json",
      "accept-language": options.locale(),
      ...extraHeaders,
    };
    let payload: BodyInit | undefined;
    if (raw) {
      headers["content-type"] = raw.contentType;
      payload = raw.body;
    } else if (body !== undefined) {
      headers["content-type"] = "application/json";
      payload = JSON.stringify(body);
    }
    const supportToken = getSupportToken();
    if (supportToken) headers[SUPPORT_HEADER] = supportToken;
    const send = (): Promise<Response> =>
      doFetch(`${base}${path}`, {
        method,
        headers,
        credentials: "include",
        ...(payload === undefined ? {} : { body: payload }),
      });
    // UX-13: idempotent GETs retry once on a network error or 5xx (local DB drops sockets).
    const retryable = method === "GET";
    let response: Response;
    try {
      response = await send();
    } catch {
      if (!retryable) throw new ApiError(0, "NETWORK_ERROR", "");
      await delay(GET_RETRY_DELAY_MS);
      try {
        response = await send();
      } catch {
        throw new ApiError(0, "NETWORK_ERROR", "");
      }
    }
    if (retryable && response.status >= 500) {
      await delay(GET_RETRY_DELAY_MS);
      try {
        response = await send();
      } catch {
        throw new ApiError(0, "NETWORK_ERROR", "");
      }
    }
    const isJson = (response.headers.get("content-type") ?? "").includes("application/json");
    const data: unknown = isJson ? await response.json().catch(() => null) : null;
    if (!response.ok) {
      const error = (data as ErrorBody | null)?.error;
      const apiError = new ApiError(
        response.status,
        typeof error?.code === "string" ? error.code : "HTTP_ERROR",
        typeof error?.message === "string" ? error.message : "",
        typeof error?.details === "object" && error.details !== null
          ? (error.details as Record<string, unknown>)
          : {},
        errorMeta(response),
      );
      noteUnauthorized(apiError, method, path);
      throw apiError;
    }
    if (response.status !== 204 && !isJson) {
      // e.g. an SPA fallback page instead of the API: treat as unreachable.
      throw new ApiError(0, "NETWORK_ERROR", "");
    }
    return data as T;
  }

  // FB2-01 request-storm guard: GETs share one in-flight call per key, and shell/global reads
  // are additionally cached for a short TTL. Errors are never cached; a successful write or a
  // workspace switch drops everything. Callers always get a private structuredClone.
  const shellCache = new Map<string, { readonly expiresAt: number; readonly data: unknown }>();
  const inflight = new Map<string, Promise<unknown>>();
  let cacheEpoch = 0;
  let lastWorkspaceId: string | null = null;
  const resetReadState = (): void => {
    cacheEpoch += 1;
    shellCache.clear();
    inflight.clear();
  };

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    raw?: { body: Blob; contentType: string },
    extraHeaders?: Record<string, string>,
  ): Promise<T> {
    const workspaceId = WORKSPACE_IN_PATH.exec(path)?.[1] ?? null;
    if (workspaceId !== null && workspaceId !== lastWorkspaceId) {
      if (lastWorkspaceId !== null) resetReadState();
      lastWorkspaceId = workspaceId;
    }
    if (method !== "GET") {
      let result: T;
      try {
        result = await performRequest<T>(method, path, body, raw, extraHeaders);
      } catch (error) {
        if (error instanceof ApiError) options.onMutationError?.(error, { method, path });
        throw error;
      }
      resetReadState();
      return result;
    }

    const key = `${method} ${path} ${options.locale()} ${JSON.stringify(extraHeaders ?? {})}`;
    const cacheable = isShellCacheable(path);
    if (cacheable) {
      const hit = shellCache.get(key);
      if (hit && hit.expiresAt > Date.now()) return structuredClone(hit.data) as T;
      if (hit) shellCache.delete(key);
    }
    let shared = inflight.get(key);
    if (!shared) {
      const epoch = cacheEpoch;
      const call: Promise<unknown> = performRequest<T>(method, path, body, raw, extraHeaders)
        .then((data) => {
          if (cacheable && epoch === cacheEpoch) {
            shellCache.set(key, { expiresAt: Date.now() + SHELL_GET_CACHE_TTL_MS, data });
          }
          return data;
        })
        .finally(() => {
          if (inflight.get(key) === call) inflight.delete(key);
        });
      shared = call;
      inflight.set(key, call);
    }
    return structuredClone(await shared) as T;
  }

  /** CSV download endpoints: same error mapping as `request`, but the success body is text. */
  async function requestCsv(method: string, path: string, body?: unknown): Promise<CsvFile> {
    let response: Response;
    try {
      response = await doFetch(`${base}${path}`, {
        method,
        headers: {
          accept: "text/csv, application/json",
          "accept-language": options.locale(),
          ...(body !== undefined ? { "content-type": "application/json" } : {}),
        },
        credentials: "include",
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
    } catch {
      throw new ApiError(0, "NETWORK_ERROR", "");
    }
    if (!response.ok) {
      const data = (await response.json().catch(() => null)) as ErrorBody | null;
      const error = data?.error;
      throw new ApiError(
        response.status,
        typeof error?.code === "string" ? error.code : "HTTP_ERROR",
        typeof error?.message === "string" ? error.message : "",
      );
    }
    const disposition = response.headers.get("content-disposition") ?? "";
    const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? "contacts.csv";
    return { filename, csv: await response.text() };
  }

  const ws = (id: string) => `/v1/workspaces/${encodeURIComponent(id)}`;

  /** Builds `?a=1&b=2`, omitting `undefined` values; `""` when nothing is present. */
  function queryString(params: Record<string, string | number | undefined> | undefined): string {
    if (!params) return "";
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) search.set(key, String(value));
    }
    const qs = search.toString();
    return qs ? `?${qs}` : "";
  }

  return { base, request, requestCsv, ws, queryString };
}

export interface HttpTransport {
  readonly base: string;
  readonly request: <T>(
    method: string,
    path: string,
    body?: unknown,
    raw?: { body: Blob; contentType: string },
    extraHeaders?: Record<string, string>,
  ) => Promise<T>;
  readonly requestCsv: (method: string, path: string, body?: unknown) => Promise<CsvFile>;
  readonly ws: (id: string) => string;
  readonly queryString: (params: Record<string, string | number | undefined> | undefined) => string;
}
